/**
 * 制作工具纯文本输入限制：禁止表情符号。
 * 位图字体与引擎字形表不支持 emoji，输入阶段直接剥离。
 */

/**
 * 判断 Unicode 码点是否为表情符号（含旗帜、符号表情等）。
 * @param cp Unicode 码点。
 */
export function isEmojiCodePoint(cp: number): boolean {
  if (cp >= 0x1f1e6 && cp <= 0x1f1ff) return true
  if (cp >= 0x1f300 && cp <= 0x1faff) return true
  if (cp >= 0x2600 && cp <= 0x27bf) return true
  if (cp >= 0x2300 && cp <= 0x23ff) return true
  if (cp >= 0x2b50 && cp <= 0x2b55) return true
  if (cp === 0x3030 || cp === 0x303d) return true
  if (cp === 0x3297 || cp === 0x3299) return true
  if (cp === 0x24c2) return true
  if (cp === 0x1f004) return true
  if (cp >= 0x1f170 && cp <= 0x1f251) return true
  if (cp === 0x20e3) return true
  return false
}

/**
 * 判断是否为表情组合用修饰符（肤色、ZWJ、变体选择符等）。
 * @param cp Unicode 码点。
 */
function isEmojiRelatedModifier(cp: number): boolean {
  if (cp === 0xfe0f || cp === 0x200d) return true
  if (cp >= 0x1f3fb && cp <= 0x1f3ff) return true
  return false
}

/**
 * 移除字符串中的表情符号及组合修饰符。
 * @param value 原始文本。
 * @returns 剥离 emoji 后的文本。
 */
export function stripEmoji(value: string): string {
  if (!value) return value
  let result = ''
  for (const char of Array.from(value)) {
    const cp = char.codePointAt(0)
    if (cp === undefined) continue
    if (isEmojiCodePoint(cp) || isEmojiRelatedModifier(cp)) continue
    result += char
  }
  return result
}

/**
 * 文本输入框是否跳过 emoji 过滤（密码、数字等）。
 * @param el 输入元素。
 */
function shouldSkipPlainTextInputGuard(el: HTMLInputElement | HTMLTextAreaElement): boolean {
  if (el instanceof HTMLInputElement) {
    const type = (el.type || 'text').toLowerCase()
    if (
      type === 'password'
      || type === 'number'
      || type === 'file'
      || type === 'hidden'
      || type === 'checkbox'
      || type === 'radio'
      || type === 'range'
      || type === 'color'
    ) {
      return true
    }
    const mode = el.inputMode
    if (mode === 'numeric' || mode === 'decimal') return true
  }
  if (el.closest('[data-plain-text-guard-skip]')) return true
  return false
}

/**
 * 校正输入框当前值，移除 emoji 并尽量保持光标位置。
 * @param el 输入元素。
 * @returns true 表示值被修改。
 */
function sanitizePlainTextInputElement(el: HTMLInputElement | HTMLTextAreaElement): boolean {
  const original = el.value
  const sanitized = stripEmoji(original)
  if (sanitized === original) return false
  const start = el.selectionStart ?? sanitized.length
  const end = el.selectionEnd ?? sanitized.length
  const nextStart = stripEmoji(original.slice(0, start)).length
  const nextEnd = stripEmoji(original.slice(0, end)).length
  el.value = sanitized
  if (typeof el.setSelectionRange === 'function') {
    el.setSelectionRange(nextStart, nextEnd)
  }
  return true
}

/**
 * 安装全局文本输入 emoji 过滤（capture 阶段，覆盖原生 input/textarea 及 Naive UI 内部输入框）。
 */
export function installPlainTextInputGuards(): void {
  if (typeof document === 'undefined') return
  document.addEventListener('input', (event) => {
    if (event.isComposing) return
    const target = event.target
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return
    if (shouldSkipPlainTextInputGuard(target)) return
    sanitizePlainTextInputElement(target)
  }, true)
}
